import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  InputAdornment,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import beAReseller from "assets/supergasbras/images/orders.png";
import { Search as SearchIcon } from "@mui/icons-material";

import manWomanBoxes from "assets/supergasbras/images/manWomanBoxes.svg";
import { CardPageHeader } from "ctech-react-components";
import Input from "components/Input";

import * as Styled from "./styles";
import ordersApi from "services/ordersApi";
import { toast } from "utils/toast";
import { OrderProps } from "store/modules/Orders/types.store";
import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";
import { useDispatch } from "react-redux";
import { cellPhoneBr, NUMBERS_REGEX } from "utils/formatByText";
import TableManagerOrders from "./components/TableManagerOrders";
import { format } from "date-fns";

interface SearchDealerData {
  phone?: string;
}

interface HeadCell {
  disablePadding: boolean;
  id: any;
  label: string | any;
  numeric: boolean;
}

interface Data {
  name: any;
  phone: string;
  idOrder: string;
  dealer: string;
  dealerId: string;
  cupom: any;
  compain: any;
  data: any;
  status: any;
}

export interface CoverageCatalogBrandsResponse {
  pagination: {
    limit: number;
    offset: number;
    count: number;
    totalCount: number;
  };
  data: CatalogDataProps[];
}

export interface CatalogDataProps {
  name: string;
  description: string;
  id: string;
}

export interface OrderDataProps {
  pagination: {
    limit: number;
    offset: number;
    count: number;
    totalCount: number;
  };
  data: OrderProps[];
}

const START_YEAR = 2023;
const DEFAULT_END_YEAR = 2030;

const getEndYear = (): number => {
  try {
    const year = new Date().getFullYear();
    if (typeof year !== "number" || Number.isNaN(year) || year < START_YEAR) {
      return DEFAULT_END_YEAR;
    }
    return year;
  } catch {
    return DEFAULT_END_YEAR;
  }
};

const getAvailableYears = (): string[] => {
  const endYear = getEndYear();
  const length = endYear - START_YEAR + 1;
  if (length < 1) return [];
  return Array.from({ length }, (_, i) => String(START_YEAR + i));
};

const FindOrders = () => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue,
  } = useForm<SearchDealerData>();

  const [dateSelected, setDateSelected] = useState<{
    label: string;
    id: string;
  }>();

  const [statusCode, setStatusCode] = useState(0);

  const dispatch = useDispatch();

  const availableYears = useMemo(() => getAvailableYears(), []);
  const date = useMemo(
    () => [
      { label: "Todos", id: "todos" },
      ...availableYears.map((year) => ({ label: year, id: year })),
    ],
    [availableYears],
  );

  const [orders, setOrders] = useState<OrderDataProps>({
    pagination: {
      limit: 11,
      offset: 1,
      count: 1,
      totalCount: 11,
    },
    data: [],
  });

  const searchOrders: SubmitHandler<SearchDealerData> = useCallback(
    async ({ phone }) => {
      if (phone && phone?.length >= 8) {
        try {
          dispatch(setIsSpinnerVisible(true));
          setOrders({
            pagination: {
              limit: 11,
              offset: 1,
              count: 1,
              totalCount: 11,
            },
            data: [],
          });
          const response = await ordersApi.get(
            `/v1/orders/getbyconsumerphone?consumerPhone=${phone}`,
          );
          dispatch(setIsSpinnerVisible(false));
          if (availableYears.includes(dateSelected?.label as string)) {
            const dataFilter = response.data.filter(
              (data: OrderProps) =>
                format(new Date(data.createdAt), "yyyy") ===
                dateSelected?.label,
            );
            setOrders((prevOrders) => ({ ...prevOrders, data: dataFilter }));
          }
          if (
            (dateSelected?.label === "Todos" || !dateSelected?.label) &&
            response.data.length
          ) {
            setOrders((prevOrders) => ({
              ...prevOrders,
              data: response.data,
            }));
          }
          setStatusCode(0);
        } catch (error: any) {
          if (error?.response.status === 404) {
            dispatch(setIsSpinnerVisible(false));
            setStatusCode(404);
          } else {
            setStatusCode(0);
            toast("Falha ao carregar lista de pedidos, tente novamente!", {
              type: "error",
            });
            dispatch(setIsSpinnerVisible(false));
          }
        }
        dispatch(setIsSpinnerVisible(false));
      } else {
        toast("O campo Telefone possui o tamanho mínimo de 8 dígitos!", {
          type: "error",
        });
      }
    },
    [dateSelected, availableYears, dispatch],
  );

  const searchDealersSubmit: SubmitHandler<SearchDealerData> = useCallback(
    ({ phone }) => {
      searchOrders({ phone: phone?.replace(/\D/g, "") });
    },
    [searchOrders],
  );

  function createData(
    name: any,
    phone: string,
    idOrder: string,
    dealer: string,
    dealerId: string,
    cupom: any,
    compain: any,
    data: any,
    status: any,
  ): Data {
    return {
      name,
      phone,
      idOrder,
      dealer,
      dealerId,
      cupom,
      compain,
      data,
      status,
    };
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: "Nome",
    },
    {
      id: "phone",
      numeric: true,
      disablePadding: false,
      label: "Telefone",
    },
    {
      id: "idOrder",
      numeric: true,
      disablePadding: false,
      label: "ID do pedido",
    },
    {
      id: "dealer",
      numeric: true,
      disablePadding: false,
      label: "Revenda",
    },
    {
      id: "cupom",
      numeric: true,
      disablePadding: false,
      label: "Cupom",
    },
    {
      id: "compain",
      numeric: true,
      disablePadding: false,
      label: "Campanha",
    },
    {
      id: "data",
      numeric: true,
      disablePadding: false,
      label: "Data",
    },
    {
      id: "status",
      numeric: true,
      disablePadding: false,
      label: "Status",
    },
    // {
    //   id: "channel",
    //   numeric: true,
    //   disablePadding: false,
    //   label: "Canal",
    // },
  ];

  const rows =
    orders?.data.map((data: OrderProps) => {
      const cellPhone =
        data?.consumer?.phone.replace(/^\+55 */, "").length === 11
          ? cellPhoneBr(data?.consumer?.phone.replace(/^\+55 */, ""))
          : data?.consumer?.phone.replace(/^\+55 */, "");

      return createData(
        data?.consumer?.name,
        cellPhone,
        data?.id,
        data?.dealer?.name,
        data?.dealer?.id,
        data?.voucher?.code ?? "-",
        data?.voucher?.campaign ?? "-",
        data?.createdAt,
        data?.status,
      );
    }) || [];

  return (
    <Styled.Container>
      <Styled.Content>
        <CardPageHeader
          urlImage={manWomanBoxes}
          title={<Typography variant="h5">{t("findOrders")}</Typography>}
        />
        <Styled.Form onSubmit={handleSubmit(searchDealersSubmit)}>
          <Typography display="flex" mb={3}>
            Encontre pedidos de um consumidor
          </Typography>
          <Styled.Row>
            <Input
              name="phone"
              label="Telefone do consumidor"
              placeholder="Buscar"
              fullWidth
              registerOptions={{
                onChange: (e) =>
                  setValue("phone", e.target.value?.replace(NUMBERS_REGEX, "")),
              }}
              sx={{ mb: 2, mr: 2, borderColor: "red" }}
              inputProps={{
                maxLength: 12,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              error={errors.phone}
              register={register}
            />
            <Styled.ActionsContainer>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                defaultValue={date[0]}
                options={date || []}
                size="small"
                onChange={(_, e) => setDateSelected(e as any)}
                renderInput={(params) => (
                  <TextField {...params} label="Selecione o Ano" />
                )}
                sx={{
                  width: 170,
                  borderColor: "red",
                }}
              />
              <Button variant="contained" type="submit">
                {t("search")}
              </Button>
            </Styled.ActionsContainer>
          </Styled.Row>
          {orders?.data.length ? (
            <TableManagerOrders headCells={headCells} rows={rows} />
          ) : (
            ""
          )}
          {statusCode === 404 ? (
            <div>
              <Typography
                variant="body1"
                textAlign="start"
                fontSize={20}
                color="#000000"
              >
                Não encontramos pedidos para o telefone &quot;
                {`${getValues("phone")}`}&quot;
              </Typography>
              <img src={beAReseller} alt="orders" />
            </div>
          ) : (
            ""
          )}
        </Styled.Form>
      </Styled.Content>
    </Styled.Container>
  );
};

export default FindOrders;
